import { useMount } from 'react-use';
import { useRouter } from 'next/router';

import { getSafeUserLocation } from '../../actions/map';
import { DEFAULT_SURFLINE_LOCATION } from '../../common/constants';
import useMapSessionLocation from './useMapSessionLocation';
import useMapLocation from './useMapLocation';
import { transformLocationParam } from '../../utils/urls/mapPaths';

/**
 * @description
 * We want to hydrate the map location from either the URL or the
 * session storage (in that order of priority)
 */
const useHydrateMapLocation = () => {
  const { doSetMapLocation } = useMapLocation();
  const router = useRouter();
  const { location } = router.query as { location: string };
  const { mapSessionLocation } = useMapSessionLocation();

  useMount(async () => {
    // Wait for router to be ready to avoid race conditions
    if (!router.isReady) {
      return;
    }

    try {
      // Path location param is the highest precedence but only available
      // on the location map pages
      if (location) {
        doSetMapLocation(transformLocationParam(location));
        return;
      }

      // Session location is the second resort for hydrating pre-existing location
      if (
        mapSessionLocation &&
        mapSessionLocation.center.lat !== 0 &&
        mapSessionLocation.center.lon !== 0
      ) {
        doSetMapLocation(mapSessionLocation);
        return;
      }

      // Use the safer user location function as last resort
      const userLocation = await getSafeUserLocation();
      doSetMapLocation(userLocation);
    } catch (_error) {
      // Fallback to default location if everything fails
      doSetMapLocation(DEFAULT_SURFLINE_LOCATION);
    }
  });
};

export default useHydrateMapLocation;
